export const ELabOrderStatus = {
  WAITING: 'WAITING',
  IN_PROGRESS: 'IN_PROGRESS',
  DONE: 'DONE',
  RETAKE: 'RETAKE',
} as const

export type TLabOrderStatus =
  (typeof ELabOrderStatus)[keyof typeof ELabOrderStatus]

/**
 * The order detail / result-entry page is only meaningful once the sample has
 * been accepted and is being processed. Orders still WAITING (not yet accepted)
 * or sent back for RETAKE (retest) have no results to view or edit.
 */
export function isLabOrderDetailAccessible(status: string): boolean {
  return status !== ELabOrderStatus.WAITING && status !== ELabOrderStatus.RETAKE
}

export const ELabOrderGender = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
} as const

export type TLabOrderGender =
  (typeof ELabOrderGender)[keyof typeof ELabOrderGender]

export interface ILabOrdersParams {
  search?: string
  donation_type_id?: string
  donor_category_id?: string
  laboratory_id?: string
  laborant_id?: string
  status?: TLabOrderStatus
  gender?: TLabOrderGender
  date_from?: string
  date_to?: string
  page: number
  size: number
}

export interface ILabOrderAnalysisSummary {
  name: string
  status: TLabOrderStatus
  loinc_code: string
}

/**
 * LOINC codes of analyses that are accepted without scanning a tube barcode
 * (ABO/Rh typing and the antibody screen are run straight from the collection
 * card). An order containing one of them can be opened from the list while
 * still WAITING and accepted through the barcode-less variant of the
 * scan-accept modal — see {@link hasBarcodeFreeAnalysis}.
 */
export const BARCODE_FREE_LOINC_CODES = ['8047-3', '70569-9'] as const

export function hasBarcodeFreeAnalysis(
  analyses: ILabOrderAnalysisSummary[] | undefined,
): boolean {
  const codes: readonly string[] = BARCODE_FREE_LOINC_CODES
  return !!analyses?.some((analysis) => codes.includes(analysis.loinc_code))
}

export interface ILabOrderTubeColor {
  id: string
  name: string
  hex_code: string
}

export interface ILabOrderItem {
  id: string
  order_number: string
  visit_id: string
  donor_id: string
  card_number: string | null
  fio: string
  birth_date: string
  gender: string
  laboratory: string
  // Observed missing on some responses despite being documented as required.
  analyses?: ILabOrderAnalysisSummary[]
  tube_color: ILabOrderTubeColor | null
  tasks_done: number
  tasks_total: number
  lab_technician: string | null
  formed_at: string
  accepted_at: string | null
  issued_at: string | null
  status: string
  status_label: string
}

export interface ILabApiError {
  code: string
  message: string
  fields: { key: string; value: string }[] | null
}

export interface ILabApiMeta {
  timestamp: string
  request_id: string
  execution_time_ms: number
}

export interface ILabApiResponse<T> {
  success: boolean
  data: T
  errors: ILabApiError[]
  meta: ILabApiMeta
}

/**
 * Counters of the analyses tab header. Each `*_trend` is the change against the
 * previous period as a percentage — negative means a drop, `0` means flat.
 */
export interface ILabAnalysesStats {
  total: number
  cbc: number
  hiv: number
  biochemistry: number
  total_trend: number
  cbc_trend: number
  hiv_trend: number
  biochemistry_trend: number
}

export type ILabAnalysesStatsResponse = ILabApiResponse<ILabAnalysesStats>

export interface ILabOrderStatusCounts {
  all: number
  WAITING: number
  IN_PROGRESS: number
  DONE: number
  RETAKE: number
}

export interface ILabOrdersResponse extends ILabApiResponse<ILabOrderItem[]> {
  page: number
  hasNext: boolean
  totalSize: number
  totalPages: number
  status_counts: ILabOrderStatusCounts
}

export interface ILabOrderByBarcodeItem {
  id: string
  order_number: string
  visit_id: string
  donor_id: string
  card_number: string | null
  fio: string
  birth_date: string
  gender: string
  laboratory: string
  requires_barcode: boolean
  status: string
  status_label: string
  formed_at: string
  accepted_at: string | null
  accepted_by_fio: string | null
  reject_reason: TLabOrderRejectReason | null
  reject_comment: string | null
}

/**
 * `GET /orders/by-barcode/{barcode}` answers 409 with this code once every
 * analysis on the tube is done — there is nothing left to accept or enter.
 */
export const LAB_BARCODE_ANALYSES_DONE_ERROR_CODE = '500649'

export type ILabOrdersByBarcodeResponse = ILabApiResponse<
  ILabOrderByBarcodeItem[]
>

export type ILabOrderActionResponse = ILabApiResponse<ILabOrderByBarcodeItem>

export const ELabOrderRejectReason = {
  HEMOLYSIS: 'HEMOLYSIS',
  CHYLOSIS: 'CHYLOSIS',
  OTHER: 'OTHER',
} as const

export type TLabOrderRejectReason =
  (typeof ELabOrderRejectReason)[keyof typeof ELabOrderRejectReason]

export interface ILabOrderRejectPayload {
  reason: TLabOrderRejectReason
  comment: string
}

export interface ILabOrderDetailAnalysisEnumValue {
  code: string
  label: string
}

export interface ILabOrderDetailAnalysisParameter {
  parameter_code: string
  name: string
  result_type: string
  unit: string | null
  required: boolean
  reference_min: number | null
  reference_max: number | null
  /**
   * Serology (S/CO) is judged against this single number instead of a range —
   * above it the backend reads the result as POSITIVE, below it as NEGATIVE.
   * `null` on every parameter that carries a reference range instead.
   */
  cutoff_value: number | null
  value: string | null
  interpretation: string | null
  flag: string | null
  enum_values: ILabOrderDetailAnalysisEnumValue[] | null
}

/**
 * Per-analysis lifecycle inside an order — distinct from `ELabOrderStatus`,
 * which tracks the order as a whole:
 * - IN_PROGRESS: accepted, results not entered yet.
 * - IN_CHECK: results submitted, awaiting validation.
 * - RETAKE: sent back for a repeat run; results must be entered again, but
 *   the analysis has to be re-started (`/analyses/{id}/start`) first.
 */
export const EAnalysisStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  IN_CHECK: 'IN_CHECK',
  RETAKE: 'RETAKE',
} as const

export type TAnalysisStatus =
  (typeof EAnalysisStatus)[keyof typeof EAnalysisStatus]

export interface ILabOrderDetailAnalysis {
  id: string
  analysis_code: string
  analysis_name: string
  status: string
  status_label: string
  result_form_type: string
  parameters: ILabOrderDetailAnalysisParameter[]
}

export interface ILabOrderDetail {
  id: string
  order_number: string
  card_number: string | null
  fio: string
  gender: string
  birth_date: string
  laboratory: string
  donor_category: string
  donation_type: string
  collected_at: string | null
  accepted_at: string | null
  issued_at: string | null
  performer_fio: string | null
  entered_at: string | null
  status: string
  status_label: string
  analyses: ILabOrderDetailAnalysis[]
}

export type ILabOrderDetailResponse = ILabApiResponse<ILabOrderDetail>

// Approving all analyses of an order returns the refreshed order detail.
export type ILabOrderApproveAllResponse = ILabApiResponse<ILabOrderDetail>

export interface ILabOrderRetestAllPayload {
  reason: string
}

// Retesting all analyses of an order returns the refreshed order detail.
export type ILabOrderRetestAllResponse = ILabApiResponse<ILabOrderDetail>

// ── Result flags ────────────────────────────────────────────────────────────

/**
 * Interpretation of a numeric result against the organization's configured
 * ranges. It is computed by the backend on `/analyses/{id}/validate` — the
 * frontend never derives it, it only colours what it is given.
 * `NO_REFERENCE` means the organization has not configured a range for that
 * parameter, so the value could not be judged at all.
 */
export const EResultFlag = {
  NORMAL: 'NORMAL',
  LOW: 'LOW',
  HIGH: 'HIGH',
  NO_REFERENCE: 'NO_REFERENCE',
} as const

export type TResultFlag = (typeof EResultFlag)[keyof typeof EResultFlag]

/** Chakra colour palette per flag — amber out-of-range, green normal, … */
export const RESULT_FLAG_COLOR: Record<string, string> = {
  [EResultFlag.NORMAL]: 'green',
  [EResultFlag.LOW]: 'yellow',
  [EResultFlag.HIGH]: 'yellow',
  [EResultFlag.NO_REFERENCE]: 'gray',
}

/**
 * Clinical meaning of a result, derived from the definition's enum values (or
 * the configured cutoff for serology). It can travel next to a flag — a
 * numeric serology result carries both.
 */
export const EResultInterpretation = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  DOUBTFUL: 'DOUBTFUL',
  NOT_INTERPRETED: 'NOT_INTERPRETED',
} as const

export type TResultInterpretation =
  (typeof EResultInterpretation)[keyof typeof EResultInterpretation]

export const RESULT_INTERPRETATION_COLOR: Record<string, string> = {
  [EResultInterpretation.POSITIVE]: 'red',
  [EResultInterpretation.NEGATIVE]: 'green',
  [EResultInterpretation.DOUBTFUL]: 'yellow',
  [EResultInterpretation.NOT_INTERPRETED]: 'gray',
}

// ── Analysis result form ────────────────────────────────────────────────────
// One call returns the definition's structure already merged with this
// organization's config, so the reference values below are the ones that apply
// to this donor's gender — nothing has to be combined client-side.
//
// The service has been observed answering in camelCase while the API doc spells
// the fields snake_case; `labApi.getAnalysisResultForm` normalizes both onto
// the camelCase shape below.

export interface ILabAnalysisResultFormEnumValue {
  /** Submitted verbatim as the result — never the label. */
  code: string
  label: string
  /** Clinical meaning attached to this value at definition level, if any. */
  interpretation: string | null
}

export interface ILabAnalysisResultFormParameter {
  parameterCode: string
  name: string
  resultType: string
  unit: string | null
  required: boolean
  referenceMin: number | null
  referenceMax: number | null
  /** See `cutoff_value` on the order-detail parameter. */
  cutoffValue: number | null
  enumValues: ILabAnalysisResultFormEnumValue[] | null
}

export interface ILabAnalysisResultForm {
  analysisId: string
  /** Structure the form was drawn from (was `analysisTemplateId`). */
  analysisDefinitionId: string
  analysisCode: string
  analysisName: string
  status: string
  gender: string
  resultFormType: string
  /**
   * The analysis is interpreted by the person entering the result rather than
   * by the backend: when true the card shows a dropdown of
   * `interpretationOptions` and submits the picked value as `interpretation`.
   */
  interpretationActive: boolean
  /** Allowed interpretation values (e.g. POSITIVE / NEGATIVE / DOUBTFUL). */
  interpretationOptions: string[]
  /**
   * Форма №129, scenario A: the backend says here whether this result is one
   * of the auto-detected infections and came back positive, so the card needs
   * no separate eligibility call to decide on the «Сформировать извещение».
   */
  emergencyNotificationEligible: boolean
  /**
   * Infection the извещение would be raised for (HIV / HBV / HCV / SYPHILIS /
   * BRUCELLOSIS); null unless eligible.
   */
  emergencyNotificationInfectionCode: string | null
  parameters: ILabAnalysisResultFormParameter[]
}

export type ILabAnalysisResultFormResponse =
  ILabApiResponse<ILabAnalysisResultForm>

// ── PCR research ────────────────────────────────────────────────────────────

export const ELabPcrStatus = {
  PENDING: 'PENDING',
  ADMITTED: 'ADMITTED',
  DEFERRED: 'DEFERRED',
} as const

export type TLabPcrStatus = (typeof ELabPcrStatus)[keyof typeof ELabPcrStatus]

export interface ILabPcrParams {
  status?: TLabPcrStatus
}

export interface ILabPcrItem {
  id: string
  visit_id: string
  donor_id: string
  card_number: string | null
  fio: string
  gender: string
  birth_date: string
  analysis_code: string
  analysis_name: string
  tube_color: ILabOrderTubeColor | null
  arrived_at: string | null
  issued_at: string | null
  lab_technician: string | null
  result: string | null
  interpretation: string | null
  interpretation_label: string | null
  status: string
  status_label: string
  confirmed_at: string | null
  confirmed_by_fio: string | null
}

export type ILabPcrResponse = ILabApiResponse<ILabPcrItem[]>

export interface ILabPcrActionPayload {
  ids: string[]
  comment?: string
}

export interface ILabPcrActionFailure {
  id: string
  code: string
  message: string
}

export interface ILabPcrActionResult {
  succeeded: ILabPcrItem[]
  failed: ILabPcrActionFailure[]
}

export type ILabPcrActionResponse = ILabApiResponse<ILabPcrActionResult>
